import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Button, Chip, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import {
  Close as CloseIcon,
  Build as BuildIcon,
  Casino as SpinIcon,
  DragHandle as TieIcon,
  Edit as EditIcon,
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  North as HigherIcon,
  PersonAdd as ForceJoinIcon,
  PlayArrow as StartIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartIcon,
  South as LowerIcon,
  Stop as EndIcon,
  Timer as TimerIcon,
  Visibility as RevealIcon,
} from "@mui/icons-material";
import HotSeatAnnounceModal, { resolveProfile } from "../../../games/HotSeatAnnounceModal";
import HolUserWheelStage from "../../../games/HolUserWheelStage";
import HolLeaderboard from "../../../games/HolLeaderboard";
import SpectatorOnOffButton from "../../SpectatorOnOffButton";
import {
  HOL,
  holFadeUp,
  holGhostBtnSx,
  holHigherBtnSx,
  holLowerBtnSx,
  holTieBtnSx,
} from "../../../UserSide/pages/holTheme";
import {
  CardImage,
  Eyebrow,
  holDangerBtnSx,
  holHostPanelSx,
  holStageSx,
  holStartSessionBtnSx,
} from "./holHostUi";
import HolConfirmDialog from "./HolConfirmDialog";
import {
  CallLog,
  ClueButton,
  DeckStrip,
  GUESS_META,
  LiveDot,
  ResultBanner,
  SpotlightCard,
  StageTracker,
  cardLabel,
} from "./HolLiveParts";

const STAGE_INDEX = { lobby: 0, selecting: 0, ready: 1, prediction: 2, reveal: 3, complete: 4 };

const getStage = (s) => {
  if (s.user_wheel) return "selecting";
  if (!s.hot_seat_device_id) return "lobby";
  const rc = s.revealed_count || 0;
  if (rc === 0) return "ready";
  if (rc >= (s.total_cards || 0)) return "complete";
  return s.pending_user_guess ? "reveal" : "prediction";
};

const WHEEL_COPY = {
  building: ["Building the Users Wheel", "Players see “Building the Users Wheel…”. New players are added automatically. Include or exclude names, then start the countdown."],
  countdown: ["Countdown on every screen", "The queue is rebuilt onto the wheel as 3 · 2 · 1 runs. It appears when the count finishes."],
  ready: ["The wheel is ready", "Spin it to pick who takes the hot seat."],
  spinning: ["Spinning…", "Whoever the wheel lands on takes the hot seat automatically."],
};

const getGuidance = (stage, { name, joinerCount, rc, rounds, score, currentCard, pending, wheelPhase }) => {
  switch (stage) {
    case "lobby":
      if (!joinerCount) {
        return {
          eyebrow: "Lobby open",
          title: "Waiting for players",
          hint: "Players join from their phones. Use Force Join to pull everyone into Higher or Lower.",
        };
      }
      return {
        eyebrow: "Lobby open",
        title: `${joinerCount} ${joinerCount === 1 ? "player" : "players"} in the queue`,
        hint:
          joinerCount >= 2
            ? "Pick the hot seat: spin the Users Wheel or select someone from the queue."
            : "Select them as the hot seat, or wait for one more player to spin the Users Wheel.",
      };
    case "selecting": {
      const [title, hint] = WHEEL_COPY[wheelPhase] || WHEEL_COPY.building;
      return { eyebrow: "Choosing the hot seat", title, hint };
    }
    case "ready":
      return {
        eyebrow: "Hot seat locked in",
        title: `${name} is in the hot seat`,
        hint: "Start the round to flip the first card on every screen.",
      };
    case "prediction":
      return {
        eyebrow: `Round ${rc} of ${rounds} · Prediction`,
        title: `Higher or lower than the ${cardLabel(currentCard)}?`,
        hint: `Ask ${name} for their call, then record it below.`,
      };
    case "reveal":
      return {
        eyebrow: `Round ${rc} of ${rounds} · Reveal`,
        title: `${name} says ${GUESS_META[pending]?.label || String(pending).toUpperCase()}`,
        hint: "The call is locked on every screen. Reveal the next card when the room is ready.",
      };
    case "complete":
      return {
        eyebrow: "Run complete · Results",
        title: `${name} scored ${score} / ${rounds}`,
        hint: "Pick the next hot seat to keep the game going, or end the game to show the final leaderboard.",
      };
    default:
      return { eyebrow: "", title: "", hint: "" };
  }
};

const bigCallSx = {
  flex: "1 1 0",
  width: "auto",
  minWidth: 0,
  minHeight: { xs: 64, sm: 80 },
  px: { xs: 1.5, sm: 3 },
  fontSize: { xs: "1.1rem", sm: "1.5rem" },
  fontWeight: 900,
  letterSpacing: "0.1em",
  borderRadius: "18px",
  "& .MuiButton-startIcon": { m: 0, mr: 1, "& > *:nth-of-type(1)": { fontSize: { xs: 26, sm: 32 } } },
};

const tieCallSx = {
  ...bigCallSx,
  flex: "0 0 auto",
  px: { xs: 1.5, sm: 2.5 },
  fontSize: { xs: "0.85rem", sm: "1rem" },
  "& .MuiButton-startIcon": { m: 0, mr: 0.5, "& > *:nth-of-type(1)": { fontSize: 22 } },
};

const revealBtnSx = {
  ...holStartSessionBtnSx,
  color: "#061018",
  background: "linear-gradient(160deg, #8cc8ff 0%, #64b4ff 55%, #3a8ad6 100%)",
  boxShadow: "0 12px 36px rgba(100,180,255,0.35)",
  "&:hover": {
    background: "linear-gradient(160deg, #a6d6ff 0%, #7cc0ff 55%, #4a9ae6 100%)",
    boxShadow: "0 16px 44px rgba(100,180,255,0.45)",
  },
  "&.Mui-disabled": { background: "rgba(100,180,255,0.2)", color: "rgba(255,255,255,0.4)" },
};

const spinBtnSx = {
  ...holStartSessionBtnSx,
  color: "#1a1208",
  background: `linear-gradient(135deg, ${HOL.lower} 0%, ${HOL.gold} 100%)`,
  boxShadow: "0 12px 36px rgba(255,107,61,0.3)",
  "&:hover": {
    background: `linear-gradient(135deg, #ff8a5c 0%, #ffe08a 100%)`,
    boxShadow: "0 16px 44px rgba(255,107,61,0.4)",
  },
  "&.Mui-disabled": { background: "rgba(255,107,61,0.2)", color: "rgba(255,255,255,0.4)" },
};

const headerBtnSx = { textTransform: "none", fontWeight: 700, borderRadius: "10px" };

const JOIN_STATUS = {
  waiting: { label: "Waiting", color: HOL.muted, bg: "rgba(255,255,255,0.06)" },
  selected: { label: "Hot seat", color: HOL.gold, bg: "rgba(245,215,110,0.14)" },
  played: { label: "Played", color: HOL.higher, bg: "rgba(168,224,99,0.12)" },
};

const PlayerAvatar = ({ name, picture, size = 36, ring }) => {
  const profile = resolveProfile(picture);
  return (
    <Avatar
      src={profile.imgSrc}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        fontWeight: 800,
        bgcolor: "rgba(255,255,255,0.1)",
        color: "#fff",
        border: ring ? `2px solid ${ring}` : "none",
      }}
    >
      {profile.isEmoji ? profile.emoji : (name || "?").trim().charAt(0).toUpperCase()}
    </Avatar>
  );
};

const SectionTitle = ({ children, right }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
    <Eyebrow>{children}</Eyebrow>
    {right}
  </Box>
);

/** Live Play with Host dashboard: one stage at a time, driven by the session state. */
const HolLiveDashboard = ({
  session,
  joiners,
  leaderboard,
  busy,
  forceActive,
  clueLoading,
  onAction,
  onForceJoin,
  onForceStop,
  onRefresh,
  onPlayClue,
  onSetClueUnlocked,
  onRequestEndGame,
  onRequestRestart,
  hotSeatAnnounce,
  onCloseHotSeatAnnounce,
  autoSpectatorSessionId,
  onAutoSpectatorHandled,
}) => {
  const spectatorRef = useRef(null);
  const queueRef = useRef(null);
  const [pendingSwitch, setPendingSwitch] = useState(null);

  useEffect(() => {
    if (!autoSpectatorSessionId) return;
    spectatorRef.current?.ensureOn(autoSpectatorSessionId);
    onAutoSpectatorHandled?.();
  }, [autoSpectatorSessionId, onAutoSpectatorHandled]);

  const stage = getStage(session);
  const deck = session.deck || [];
  const totalCards = session.total_cards || 0;
  const rc = session.revealed_count || 0;
  const rounds = Math.max(0, totalCards - 1);
  const score = session.score ?? 0;
  const pending = session.pending_user_guess || null;
  const hotSeatId = session.hot_seat_device_id || null;
  const hotSeatName = session.hot_seat_display_name || "The player";
  const hotSeatJoiner = joiners.find((j) => j.device_id === hotSeatId);
  const wheel = session.user_wheel || null;
  const wheelPhase = wheel?.phase || null;
  const wheelLocked = wheelPhase === "countdown" || wheelPhase === "spinning";
  const eligibleCount = (wheel?.entries || []).filter((e) => !e.is_excluded).length;
  const currentCard = rc >= 1 ? deck[rc - 1] : null;
  const canClue = session.status === "playing" && rc >= 1 && rc < totalCards;
  const clueOpen = session.clue_available !== false;
  const clueButtonShown = session.clue_user_can_play !== false;
  const clueUnlocked = session.clue_unlocked === true;
  const clueMax = session.clue_max_plays ?? Math.max(0, totalCards - 1);
  const clueLeft = session.clue_plays_left ?? clueMax;
  const runInProgress = !!hotSeatId && rc >= 1 && rc < totalCards;
  const runKey = `${session.id}-${hotSeatId || "none"}`;
  const guidance = getGuidance(stage, {
    name: hotSeatName,
    joinerCount: joiners.length,
    rc,
    rounds,
    score,
    currentCard,
    pending,
    wheelPhase,
  });

  const selectHotSeat = (j) =>
    onAction("select-hot-seat", { device_id: j.device_id, display_name: j.display_name });

  const requestSelect = (j) => (runInProgress ? setPendingSwitch({ kind: "select", joiner: j }) : selectHotSeat(j));
  const requestWheel = () => (runInProgress ? setPendingSwitch({ kind: "wheel" }) : onAction("build-user-wheel"));

  const confirmSwitch = async () => {
    const next = pendingSwitch;
    setPendingSwitch(null);
    if (next?.kind === "select") await selectHotSeat(next.joiner);
    else if (next?.kind === "wheel") await onAction("build-user-wheel");
  };

  const scrollToQueue = () => queueRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const renderLobby = () => (
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-end", mb: 3, height: { xs: 120, sm: 150 } }}>
        {[-12, 0, 12].map((tilt, i) => (
          <CardImage key={tilt} width={{ xs: 78, sm: 100 }} tilt={tilt} sx={{ mx: -1.5, mb: i === 1 ? 2 : 0 }} />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
        {!joiners.length && !forceActive ? (
          <Button startIcon={<ForceJoinIcon />} disabled={busy} onClick={onForceJoin} sx={holStartSessionBtnSx}>
            Force Join players
          </Button>
        ) : null}
        <Button startIcon={<SpinIcon />} disabled={busy || joiners.length < 2} onClick={requestWheel} sx={spinBtnSx}>
          Spin Users Wheel
        </Button>
      </Box>
      <Typography sx={{ color: HOL.muted, fontSize: "0.82rem", mt: 1.5 }}>
        {joiners.length < 2
          ? "The Users Wheel needs at least 2 players in the queue."
          : "Or tap Select next to a name in the queue."}
      </Typography>
    </Box>
  );

  const renderWheel = () => (
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <HolUserWheelStage userWheel={wheel} size={340} preview />
      </Box>
      {!wheelLocked ? (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 2, justifyContent: "center" }}>
            {(wheel.entries || []).map((entry) => (
              <Chip
                key={entry.device_id || entry.id}
                label={entry.display_name}
                size="small"
                clickable
                disabled={busy}
                color={entry.is_excluded ? "default" : "warning"}
                variant={entry.is_excluded ? "outlined" : "filled"}
                onClick={() =>
                  onAction("user-wheel-exclude", { device_id: entry.device_id, excluded: !entry.is_excluded })
                }
                sx={{
                  fontWeight: 700,
                  opacity: entry.is_excluded ? 0.45 : 1,
                  textDecoration: entry.is_excluded ? "line-through" : "none",
                }}
              />
            ))}
          </Box>
          <Typography sx={{ color: HOL.muted, fontSize: "0.78rem", mt: 1 }}>
            {eligibleCount} eligible · tap a name to include or exclude. Anyone who joins is added before the wheel appears.
          </Typography>
        </>
      ) : null}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 2.5, justifyContent: "center" }}>
        {wheelPhase === "building" ? (
          <Button
            startIcon={<TimerIcon />}
            disabled={busy || eligibleCount < 2}
            onClick={() => onAction("user-wheel-countdown")}
            sx={revealBtnSx}
          >
            Start countdown 3 · 2 · 1
          </Button>
        ) : null}
        {wheelPhase === "ready" ? (
          <Button
            startIcon={<SpinIcon />}
            disabled={busy || eligibleCount < 2}
            onClick={() => onAction("spin-user-wheel")}
            sx={spinBtnSx}
          >
            Spin the wheel
          </Button>
        ) : null}
        {!wheelLocked ? (
          <Button
            variant="outlined"
            startIcon={<BuildIcon />}
            disabled={busy}
            onClick={() => onAction("build-user-wheel")}
            sx={holGhostBtnSx}
          >
            Rebuild
          </Button>
        ) : null}
        <Button
          variant="text"
          startIcon={<CloseIcon />}
          disabled={busy}
          onClick={() => onAction("cancel-user-wheel")}
          sx={{ ...holGhostBtnSx, border: "none" }}
        >
          Cancel wheel
        </Button>
      </Box>
    </Box>
  );

  const renderCenter = () => {
    if (stage === "reveal") {
      const g = GUESS_META[pending] || GUESS_META.higher;
      return (
        <Box sx={{ textAlign: "center", animation: `${holFadeUp} 0.35s ease both` }} key={`call-${rc}-${pending}`}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.14em", color: HOL.muted }}>
            CALL LOCKED
          </Typography>
          <Box
            sx={{
              mt: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 2,
              py: 1,
              borderRadius: "16px",
              bgcolor: `${g.color}1f`,
              border: `2px solid ${g.color}`,
              color: g.color,
            }}
          >
            <g.Icon sx={{ fontSize: { xs: 26, sm: 32 } }} />
            <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: { xs: "1.2rem", sm: "1.6rem" } }}>
              {g.label}
            </Typography>
          </Box>
        </Box>
      );
    }
    if ((stage === "prediction" || stage === "complete") && session.last_result && rc >= 2) {
      return <ResultBanner result={session.last_result} roundNumber={rc - 1} animKey={`${runKey}-${rc}`} />;
    }
    return (
      <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: { xs: "1.4rem", sm: "2rem" }, color: "rgba(255,255,255,0.35)" }}>
        VS
      </Typography>
    );
  };

  const renderControls = () => {
    if (stage === "ready") {
      return (
        <Button
          startIcon={<StartIcon />}
          disabled={busy}
          onClick={() => onAction("reveal-first")}
          sx={{ ...holStartSessionBtnSx, minWidth: { xs: "100%", sm: 340 } }}
        >
          Start Round
        </Button>
      );
    }
    if (stage === "prediction") {
      return (
        <Box sx={{ display: "flex", gap: { xs: 1, sm: 1.5 }, width: "100%", maxWidth: 680 }}>
          <Button
            variant="contained"
            startIcon={<HigherIcon />}
            disabled={busy}
            onClick={() => onAction("set-guess", { guess: "higher" })}
            sx={{ ...holHigherBtnSx, ...bigCallSx }}
          >
            Higher
          </Button>
          <Button
            variant="contained"
            startIcon={<TieIcon />}
            disabled={busy}
            onClick={() => onAction("set-guess", { guess: "tie" })}
            sx={{ ...holTieBtnSx, ...tieCallSx }}
          >
            Tie
          </Button>
          <Button
            variant="contained"
            startIcon={<LowerIcon />}
            disabled={busy}
            onClick={() => onAction("set-guess", { guess: "lower" })}
            sx={{ ...holLowerBtnSx, ...bigCallSx }}
          >
            Lower
          </Button>
        </Box>
      );
    }
    if (stage === "reveal") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Button
            startIcon={<RevealIcon />}
            disabled={busy}
            onClick={() => onAction("reveal-next")}
            sx={{ ...revealBtnSx, minWidth: { xs: "100%", sm: 340 } }}
          >
            Reveal next card
          </Button>
          <Button size="small" startIcon={<EditIcon />} disabled={busy} onClick={() => onAction("clear-guess")} sx={holGhostBtnSx}>
            Change call
          </Button>
        </Box>
      );
    }
    if (stage === "complete") {
      return (
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", justifyContent: "center" }}>
          <Button startIcon={<SpinIcon />} disabled={busy || joiners.length < 2} onClick={requestWheel} sx={spinBtnSx}>
            Spin Users Wheel
          </Button>
          <Button variant="outlined" onClick={scrollToQueue} sx={{ ...holGhostBtnSx, px: 2.5 }}>
            Choose from queue
          </Button>
          <Button startIcon={<EndIcon />} disabled={busy} onClick={onRequestEndGame} sx={{ ...holDangerBtnSx, py: 1.25 }}>
            End Game
          </Button>
        </Box>
      );
    }
    return null;
  };

  const renderRun = () => (
    <Box>
      {canClue ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Typography sx={{ color: HOL.muted, fontSize: "0.85rem", fontWeight: 700 }}>
            Clues left {clueLeft} / {clueMax}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={busy || !clueOpen}
            onClick={() => onSetClueUnlocked?.(!clueUnlocked)}
            sx={{ ...holGhostBtnSx, px: 1.5, color: clueUnlocked ? HOL.gold : "#fff" }}
          >
            {clueUnlocked ? "Lock clue" : "Unlock clue"}
          </Button>
          <Typography sx={{ color: HOL.muted, fontSize: "0.78rem", width: "100%", textAlign: "center" }}>
            {clueUnlocked
              ? clueButtonShown
                ? "Clue is unlocked for this call. You and the hot seat can play it."
                : "Clue is unlocked. You can play it. The button stays hidden on player phones."
              : clueButtonShown
                ? "Clue is locked. Unlock it before you or the hot seat can play it."
                : "Clue is locked. Unlock it to play it here. Player phones stay hidden."}
          </Typography>
        </Box>
      ) : null}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 1.5, sm: 3, md: 5 },
        }}
      >
        <SpotlightCard
          card={currentCard}
          cardKey={`${runKey}-${rc}`}
          label={stage === "ready" ? "FIRST CARD" : stage === "complete" ? "LAST CARD" : "CURRENT CARD"}
          mystery={stage === "ready"}
          footer={canClue ? <ClueButton onClick={() => onPlayClue("visible")} disabled={clueLoading || !clueOpen || !clueUnlocked} /> : null}
        />
        {stage !== "ready" ? renderCenter() : null}
        {stage === "prediction" || stage === "reveal" ? (
          <SpotlightCard
            card={null}
            cardKey={`${runKey}-next-${rc}`}
            label="NEXT CARD"
            accent="#64b4ff"
            mystery
            footer={canClue ? <ClueButton onClick={() => onPlayClue("hidden")} disabled={clueLoading || !clueOpen || !clueUnlocked} /> : null}
          />
        ) : null}
        {stage === "complete" ? (
          <Box
            sx={{
              textAlign: "center",
              px: { xs: 2, sm: 3 },
              py: 2.5,
              borderRadius: "18px",
              border: `1px solid ${HOL.gold}55`,
              bgcolor: "rgba(245,215,110,0.08)",
              animation: `${holFadeUp} 0.45s ease both`,
            }}
          >
            <TrophyIcon sx={{ color: HOL.gold, fontSize: 34 }} />
            <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: { xs: "2.2rem", sm: "2.8rem" }, color: HOL.gold, lineHeight: 1 }}>
              {score}
              <Box component="span" sx={{ fontSize: "1.1rem", color: HOL.muted }}>
                {" "}/ {rounds}
              </Box>
            </Typography>
            <Typography sx={{ color: HOL.muted, fontSize: "0.8rem", mt: 0.75 }}>Final score</Typography>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>{renderControls()}</Box>

      <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${HOL.panelBorder}` }}>
        <Typography sx={{ textAlign: "center", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.14em", color: HOL.muted, mb: 1.5 }}>
          THE RUN · {Math.min(rc, totalCards)} OF {totalCards} CARDS REVEALED
        </Typography>
        <DeckStrip deck={deck} revealedCount={rc} totalCards={totalCards} rounds={session.current_rounds || []} runKey={runKey} />
        <Box sx={{ mt: 1.75 }}>
          <CallLog rounds={session.current_rounds || []} pendingGuess={pending} pendingRound={rc} />
        </Box>
      </Box>
    </Box>
  );

  const renderStageBody = () => {
    if (stage === "lobby") return renderLobby();
    if (stage === "selecting") return renderWheel();
    return renderRun();
  };

  return (
    <Box>
      <HotSeatAnnounceModal
        open={!!hotSeatAnnounce}
        onClose={onCloseHotSeatAnnounce}
        displayName={hotSeatAnnounce?.display_name}
        profilePicture={hotSeatAnnounce?.profile_picture}
      />

      <Box sx={{ ...holHostPanelSx, py: 1.5, mb: 2, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
        <LiveDot />
        <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: { xs: "1rem", sm: "1.2rem" }, letterSpacing: "0.04em" }}>
          HIGHER OR LOWER
        </Typography>
        <Chip size="small" label={`Session #${session.id}`} sx={{ bgcolor: "rgba(255,255,255,0.06)", fontWeight: 700 }} />
        <Chip
          size="small"
          icon={<GroupsIcon sx={{ fontSize: 16 }} />}
          label={`${joiners.length} in queue`}
          sx={{ bgcolor: "rgba(255,255,255,0.06)", fontWeight: 700 }}
        />
        <Chip size="small" label={`${totalCards} cards`} sx={{ bgcolor: "rgba(255,255,255,0.06)", fontWeight: 700 }} />
        <Box sx={{ flex: 1 }} />
        {forceActive ? (
          <Button variant="outlined" color="warning" size="small" disabled={busy} onClick={onForceStop} sx={headerBtnSx}>
            Force Stop
          </Button>
        ) : (
          <Button variant="contained" color="secondary" size="small" disabled={busy} onClick={onForceJoin} sx={headerBtnSx}>
            Force Join (MYM)
          </Button>
        )}
        <SpectatorOnOffButton
          ref={spectatorRef}
          gameType="higher_or_lower"
          gameLabel="Higher or Lower"
          sessionId={session.id}
          disabled={busy}
        />
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={busy} sx={{ color: HOL.muted }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Restart session">
          <span>
            <IconButton size="small" onClick={onRequestRestart} disabled={busy} sx={{ color: HOL.muted }}>
              <RestartIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button startIcon={<EndIcon />} size="small" disabled={busy} onClick={onRequestEndGame} sx={{ ...holDangerBtnSx, px: 2 }}>
          End Game
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Box sx={{ ...holStageSx, p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
              <StageTracker activeIndex={STAGE_INDEX[stage]} />
              {hotSeatId && rc >= 1 && stage !== "selecting" ? (
                <Box sx={{ display: "flex", gap: 2, alignItems: "baseline" }}>
                  <Typography sx={{ fontSize: "0.78rem", color: HOL.muted, fontWeight: 700 }}>
                    ROUND{" "}
                    <Box component="span" sx={{ color: "#fff", fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: "1.05rem" }}>
                      {Math.min(rc, rounds)}/{rounds}
                    </Box>
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: HOL.muted, fontWeight: 700 }}>
                    SCORE{" "}
                    <Box component="span" sx={{ color: HOL.gold, fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: "1.05rem" }}>
                      {score}
                    </Box>
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Box key={`${stage}-${wheelPhase}`} sx={{ textAlign: "center", mb: 3, animation: `${holFadeUp} 0.35s ease both` }}>
              <Eyebrow>{guidance.eyebrow}</Eyebrow>
              <Typography
                sx={{
                  fontFamily: HOL.fontDisplay,
                  fontWeight: 900,
                  fontSize: { xs: "1.6rem", sm: "2.2rem", md: "2.5rem" },
                  lineHeight: 1.1,
                  mt: 0.75,
                }}
              >
                {guidance.title}
              </Typography>
              <Typography sx={{ color: HOL.muted, mt: 1, maxWidth: 560, mx: "auto" }}>{guidance.hint}</Typography>
            </Box>

            {renderStageBody()}
          </Box>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={holHostPanelSx}>
              <SectionTitle>Hot seat</SectionTitle>
              {hotSeatId ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <PlayerAvatar name={hotSeatName} picture={hotSeatJoiner?.profile_picture} size={52} ring={HOL.gold} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800, fontSize: "1.15rem" }}>
                      {hotSeatName}
                    </Typography>
                    <Typography sx={{ color: HOL.muted, fontSize: "0.8rem" }}>
                      {stage === "complete" || (rc >= totalCards && totalCards > 0)
                        ? "Run complete"
                        : rc
                          ? `Round ${Math.min(rc, rounds)} of ${rounds}`
                          : "Waiting for the first card"}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: "1.8rem", color: HOL.gold, lineHeight: 1 }}>
                      {score}
                    </Typography>
                    <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.1em", color: HOL.muted }}>
                      POINTS
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ color: HOL.muted, fontSize: "0.9rem" }}>
                  No one yet. Choose from the queue or spin the Users Wheel.
                </Typography>
              )}
            </Box>

            <Box ref={queueRef} sx={holHostPanelSx}>
              <SectionTitle
                right={
                  !wheel ? (
                    <Button
                      size="small"
                      startIcon={<SpinIcon sx={{ fontSize: 16 }} />}
                      disabled={busy || joiners.length < 2}
                      onClick={requestWheel}
                      sx={{ ...headerBtnSx, color: HOL.gold }}
                    >
                      Users Wheel
                    </Button>
                  ) : null
                }
              >
                Join queue · {joiners.length}
              </SectionTitle>
              {!joiners.length ? (
                <Typography sx={{ color: HOL.muted, fontSize: "0.9rem" }}>No players have joined yet.</Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 340, overflowY: "auto", pr: 0.5 }}>
                  {joiners.map((j) => {
                    const isHotSeat = j.device_id === hotSeatId;
                    const status = JOIN_STATUS[isHotSeat ? "selected" : j.join_status] || JOIN_STATUS.waiting;
                    return (
                      <Box
                        key={j.device_id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          borderRadius: "12px",
                          border: `1px solid ${isHotSeat ? `${HOL.gold}55` : HOL.panelBorder}`,
                          bgcolor: isHotSeat ? "rgba(245,215,110,0.08)" : "rgba(255,255,255,0.02)",
                        }}
                      >
                        <PlayerAvatar name={j.display_name} picture={j.profile_picture} size={32} />
                        <Typography noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                          {j.display_name}
                        </Typography>
                        <Chip
                          size="small"
                          label={status.label}
                          sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, color: status.color, bgcolor: status.bg }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy || wheelLocked || (isHotSeat && runInProgress)}
                          onClick={() => requestSelect(j)}
                          sx={{ ...headerBtnSx, minWidth: 0, px: 1.25, borderColor: "rgba(255,255,255,0.2)", color: "#fff" }}
                        >
                          {isHotSeat && runInProgress ? "Playing" : "Select"}
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            <Box sx={holHostPanelSx}>
              <SectionTitle>Live leaderboard</SectionTitle>
              <HolLeaderboard
                entries={leaderboard}
                highlightDeviceId={hotSeatId}
                maxRows={8}
                dense
                emptyText="Scores appear here after each finished run."
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <HolConfirmDialog
        open={!!pendingSwitch}
        tone="primary"
        title="Pick a new hot seat?"
        confirmLabel={pendingSwitch?.kind === "wheel" ? "Build Users Wheel" : `Select ${pendingSwitch?.joiner?.display_name || "player"}`}
        onConfirm={confirmSwitch}
        onCancel={() => setPendingSwitch(null)}
        busy={busy}
      >
        {hotSeatName} is mid-run ({score} after {Math.max(0, rc - 1)} of {rounds} rounds). Picking someone else
        discards this run and it won&apos;t count on the leaderboard.
      </HolConfirmDialog>
    </Box>
  );
};

export default HolLiveDashboard;
